import * as React from 'react';
import dayjs from 'dayjs';
import { EVENT_IMAGE } from 'Constants';
import useDeviceDetect from 'Hooks/useDeviceDetect';
import { isNullOrUndefined } from 'Util/TypeGuards';
import alertToast from 'Util/ToastifyUtils';
import { EventDetailEntity } from 'Models/Entities';
import { GetFormattedDateTime } from './EventUtils';
import { EventOccurrence, EventSeriesWithNextOccurrence } from 'Services/Api/_HumanWritten/EventSeriesEntityService';

import If from '../../../If/If';
import Icon from '../../Icon/Icon';
import { Link, useNavigate } from 'react-router-dom';
import { store } from 'Models/Store';
import { Button, Colors, Display, Sizes } from '../../../Button/Button';

export interface EventCardProps {
	event: EventDetailEntity;
	index: number;
	eventToEdit: React.MutableRefObject<EventDetailEntity | null>;
}

export interface EventCardFromSeriesProps {
	eventSeries: EventSeriesWithNextOccurrence;
	index: number;
}

function htmlToPlainText(html: string): string {
	const parser = new DOMParser();
	const doc = parser.parseFromString(html, 'text/html');
	return doc.body.textContent || '';
}

export function EventCard(props: EventCardProps) {
	const { event, index, eventToEdit } = props;
	return (
		<div className="event-card__container">
			<EventCardDetails event={event} index={index} eventToEdit={eventToEdit} />
		</div>
	);
}

function EventCardDetails({ event, index, eventToEdit }: EventCardProps) {
	const { isIpad } = useDeviceDetect();
	const navigate = useNavigate();

	if (isNullOrUndefined(event) || isNullOrUndefined(event.ferryTrip)) {
		alertToast('Event is missing details', 'error', undefined, { autoClose: 3000 });
		return <></>;
	}

	return (
		<div className="event-details__container">
			<Link
				to={`/event-details/${event.id}`}
				className="event-details__link hide-underline"
				id={event.id}
				tabIndex={index}
			>
				<div className="event-details__image">
					<img
						className="event-detail__header-img"
						src={EVENT_IMAGE + event.headerImage?.id}
						alt="header-img"
					/>
					<p className="event-details__label">
						{event.eventLabel?.iconName && (
							<Icon classname="event-icon" name={event.eventLabel.iconName} useMaterialIcons />
						)}
						{`${event.eventLabel?.label} ${event.hidden ? '(private)' : ''}`}
					</p>
					<p className="event-capacity__label">
						<div className="issue-icon white-icon" />
						<If condition={(event?.remainingCapacity ?? 0) === 0}>Sold out</If>
						<If condition={(event?.remainingCapacity ?? 0) > 0 && !event.closed}>
							Tickets left: {event?.remainingCapacity ?? 0}/{event?.totalCapacity ?? 0}
						</If>
						<If condition={(event?.remainingCapacity ?? 0) > 0 && event.closed}>Closed</If>
					</p>
				</div>
				<div className="event-details__body" key={event.id}>
					<div className="event-details__header">
						<h5 className="event-details__name">{event.name}</h5>
					</div>
					<p className="event-details__date-time">
						{GetFormattedDateTime(
							event.ferryTrip.startDate,
							event.ferryTrip.endDate,
							event.ferryTrip.startTime,
							event.ferryTrip.endTime,
						)}
					</p>
					{/* eslint-disable-next-line react/no-danger */}
					<p className="event-details__description">{htmlToPlainText(event.description ?? '')}</p>
					<p className="event-details__see-more">See more</p>
				</div>
			</Link>
			<If condition={store.isManager && !isIpad}>
				<Button
					className="event-details__edit-event"
					sizes={Sizes.Medium}
					colors={Colors.Secondary}
					display={Display.Text}
					icon={{ icon: 'edit-box', iconPos: 'icon-bottom' }}
					onClick={e => {
						e.stopPropagation();
						eventToEdit.current = event;
						navigate(`/upcoming-events/edit/${event.id}`);
					}}
				/>
			</If>
		</div>
	);
}

export function EventCardFromSeries({ eventSeries, index }: EventCardFromSeriesProps) {
	return (
		<div className="event-card__container">
			<EventCardFromSeriesDetails eventSeries={eventSeries} index={index} />
		</div>
	);
}

function EventCardFromSeriesDetails({ eventSeries, index }: EventCardFromSeriesProps) {
	const { isIpad } = useDeviceDetect();
	const navigate = useNavigate();

	if (isNullOrUndefined(eventSeries) || !eventSeries.hasUpcomingOccurrence) {
		alertToast('Event series is missing details', 'error', undefined, { autoClose: 3000 });
		return <></>;
	}

	const handleEditClick = (e: React.MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();
		navigate(`/upcoming-events/edit/${eventSeries.id}`);
	};

	const handleCheckInClick = (e: React.MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();
		navigate(`/event-check-in/${eventSeries.nextOccurrenceId}`);
	};

	// Check if this event has multiple occurrences
	const hasMultipleOccurrences = (eventSeries.upcomingOccurrenceCount ?? 0) > 1;
	const upcomingOccurrences = eventSeries.upcomingOccurrences ?? [];

	// Parse date strings to Date objects for formatting (single occurrence only)
	const startDate = eventSeries.startDate ? new Date(eventSeries.startDate) : undefined;
	const endDate = eventSeries.endDate ? new Date(eventSeries.endDate) : undefined;
	const startTime = eventSeries.startTime ? new Date(`1970-01-01T${eventSeries.startTime}`) : undefined;
	const endTime = eventSeries.endTime ? new Date(`1970-01-01T${eventSeries.endTime}`) : undefined;

	return (
		<div className="event-details__container">
			<Link
				to={`/event-details/${eventSeries.id}`}
				className="event-details__link hide-underline"
				id={eventSeries.id}
				tabIndex={index}
			>
				<div className="event-details__image">
					<img
						className="event-detail__header-img"
						src={EVENT_IMAGE + eventSeries.headerImage?.id}
						alt="header-img"
					/>
					<p className="event-details__label">
						{eventSeries.eventLabel?.iconName && (
							<Icon classname="event-icon" name={eventSeries.eventLabel.iconName} useMaterialIcons />
						)}
						{`${eventSeries.eventLabel?.label ?? ''} ${eventSeries.hidden ? '(private)' : ''}`}
					</p>
					<If condition={!hasMultipleOccurrences}>
						<p className="event-capacity__label">
							<div className="issue-icon white-icon" />
							<If condition={(eventSeries.remainingCapacity ?? 0) === 0}>Sold out</If>
							<If condition={(eventSeries.remainingCapacity ?? 0) > 0 && !eventSeries.closed}>
								Tickets left: {eventSeries.remainingCapacity ?? 0}/{eventSeries.totalCapacity ?? 0}
							</If>
							<If condition={(eventSeries.remainingCapacity ?? 0) > 0 && eventSeries.closed}>Closed</If>
						</p>
					</If>
				</div>
				<div className="event-details__body" key={eventSeries.id}>
					<div className="event-details__header">
						<h5 className="event-details__name">{eventSeries.name}</h5>
					</div>
					<If condition={hasMultipleOccurrences}>
						<OccurrenceDatePills
							occurrences={upcomingOccurrences}
							totalCount={eventSeries.upcomingOccurrenceCount ?? upcomingOccurrences.length}
						/>
					</If>
					<If condition={!hasMultipleOccurrences && !!startDate && !!endDate && !!startTime && !!endTime}>
						<p className="event-details__date-time">
							{GetFormattedDateTime(startDate!, endDate!, startTime!, endTime!)}
						</p>
					</If>
					<p className="event-details__description">{htmlToPlainText(eventSeries.description ?? '')}</p>
					<p className="event-details__see-more">See more</p>
				</div>
			</Link>
			<If condition={store.isStaff && !isIpad}>
				<div className="event-card__staff-actions">
					<Button
						className="event-card__staff-btn icon-edit-box icon-left"
						display={Display.Text}
						colors={Colors.None}
						sizes={Sizes.Small}
						onClick={handleEditClick}
					>
						Edit
					</Button>
					<If condition={store.can('AccessCheckIn')}>
						<Button
							className="event-card__staff-btn icon-check-box icon-left"
							display={Display.Text}
							colors={Colors.None}
							sizes={Sizes.Small}
							onClick={handleCheckInClick}
						>
							Check-in
						</Button>
					</If>
				</div>
			</If>
		</div>
	);
}

interface OccurrenceDatePillsProps {
	occurrences: EventOccurrence[];
	totalCount?: number;
	maxPills?: number;
}

function OccurrenceDatePills({ occurrences, totalCount, maxPills = 3 }: OccurrenceDatePillsProps) {
	const total = totalCount ?? occurrences.length;
	const hasMore = total > maxPills;
	const pillsToShow = hasMore ? maxPills - 1 : occurrences.length;
	const displayOccurrences = occurrences.slice(0, pillsToShow);
	const remainingCount = total - pillsToShow;

	return (
		<div className="event-details__date-pills">
			{displayOccurrences.map(occ => {
				const date = dayjs(occ.startDate);
				const time = dayjs(`1970-01-01T${occ.startTime}`);
				return (
					<span
						key={occ.id}
						className={`event-details__date-pill ${occ.isSoldOut ? 'event-details__date-pill--sold-out' : ''}`}
					>
						{date.format('D MMM')} {time.format('h:mma')}
					</span>
				);
			})}
			{remainingCount > 0 && (
				<span className="event-details__date-pill event-details__date-pill--more">+{remainingCount} more</span>
			)}
		</div>
	);
}
